import React, { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { formatIstTimeOnly, formatIstFullDateTime } from '../chartUtils';
import { finiteNumber } from '../format';

const PHASE_COLORS = { r: '#d94343', y: '#a76b00', b: '#2474c6', avg: '#087f73' };

const CHARTS = [
  {
    id: 'current',
    label: 'Current',
    unit: 'A',
    hasPhases: true,
    rangePath: ['current', 'avg'],
    series: [
      { key: 'current_r', dataKey: 'r', label: 'R phase', color: PHASE_COLORS.r },
      { key: 'current_y', dataKey: 'y', label: 'Y phase', color: PHASE_COLORS.y },
      { key: 'current_b', dataKey: 'b', label: 'B phase', color: PHASE_COLORS.b },
      { key: 'current_avg', dataKey: 'avg', label: 'Average', color: PHASE_COLORS.avg, average: true },
    ],
  },
  {
    id: 'voltage',
    label: 'Voltage',
    unit: 'V',
    hasPhases: true,
    rangePath: ['voltage', 'avg'],
    series: [
      { key: 'voltage_r', dataKey: 'r', label: 'R phase', color: PHASE_COLORS.r },
      { key: 'voltage_y', dataKey: 'y', label: 'Y phase', color: PHASE_COLORS.y },
      { key: 'voltage_b', dataKey: 'b', label: 'B phase', color: PHASE_COLORS.b },
      { key: 'voltage_avg', dataKey: 'avg', label: 'Average', color: PHASE_COLORS.avg, average: true },
    ],
  },
  {
    id: 'pf',
    label: 'PF',
    unit: '',
    hasPhases: true,
    rangePath: ['pf', 'avg'],
    series: [
      { key: 'pf_r', dataKey: 'r', label: 'R phase', color: PHASE_COLORS.r },
      { key: 'pf_y', dataKey: 'y', label: 'Y phase', color: PHASE_COLORS.y },
      { key: 'pf_b', dataKey: 'b', label: 'B phase', color: PHASE_COLORS.b },
      { key: 'pf_avg', dataKey: 'avg', label: 'Average', color: PHASE_COLORS.avg, average: true },
    ],
  },
  {
    id: 'power',
    label: 'Power',
    unit: 'kW',
    hasPhases: false,
    rangePath: ['power', 'total_kw'],
    series: [{ key: 'total_kw', dataKey: 'avg', label: 'Total power', color: '#087f73', average: true }],
  },
  {
    id: 'flow',
    label: 'Flow',
    unit: 'm³/h',
    hasPhases: false,
    rangePath: ['flow', 'flowrate'],
    series: [{ key: 'flowrate', dataKey: 'avg', label: 'Flow', color: '#2474a8', average: true }],
  },
];

function CustomChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload || !payload.length) return null;
  const timeText = label ? formatIstFullDateTime(label) : '';

  return (
    <div className="prediction-chart-tooltip">
      {timeText && <div className="prediction-chart-tooltip-time">{timeText} IST</div>}
      {payload.map((item, idx) => {
        if (item.value === null || item.value === undefined) return null;
        return (
          <div key={idx} className="prediction-chart-tooltip-row">
            <span style={{ color: item.color, fontWeight: 600 }}>{item.name}:</span>
            <span style={{ fontWeight: 700 }}>
              {typeof item.value === 'number' ? item.value.toFixed(3) : item.value}
              {unit ? ` ${unit}` : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function TelemetryCharts({ payload }) {
  const [activeId, setActiveId] = useState('current');
  const [showPhases, setShowPhases] = useState(false);

  const chart = CHARTS.find((item) => item.id === activeId) || CHARTS[0];
  const trend = payload?.trend_series;
  const timestamps = trend?.timestamps;

  // Extract baseline specifications
  const ranges = payload?.baseline_ranges || {};
  const rangeGroup = ranges[chart.rangePath[0]];
  const spec = rangeGroup ? rangeGroup[chart.rangePath[1]] : null;
  const baselineMedian = spec?.available !== false ? finiteNumber(spec?.median) : null;
  const p05 = spec?.available !== false ? finiteNumber(spec?.p05 ?? spec?.expected_low) : null;
  const p95 = spec?.available !== false ? finiteNumber(spec?.p95 ?? spec?.expected_high) : null;

  // Prepare chart data points
  const chartData = useMemo(() => {
    if (!timestamps || !timestamps.length || !trend) return [];

    return timestamps
      .map((stamp, index) => {
        if (!stamp) return null;
        const pt = {
          rawTime: stamp,
          timeDisplay: formatIstTimeOnly(stamp),
        };

        if (chart.hasPhases) {
          const rVal = finiteNumber(trend[`${chart.id}_r`]?.[index]);
          const yVal = finiteNumber(trend[`${chart.id}_y`]?.[index]);
          const bVal = finiteNumber(trend[`${chart.id}_b`]?.[index]);
          const avgVal = finiteNumber(trend[`${chart.id}_avg`]?.[index]);

          pt.r = rVal;
          pt.y = yVal;
          pt.b = bVal;
          pt.avg = avgVal;
        } else {
          const valKey = chart.id === 'power' ? 'total_kw' : 'flowrate';
          const val = finiteNumber(trend[valKey]?.[index]);
          pt.avg = val;
        }
        return pt;
      })
      .filter(Boolean);
  }, [timestamps, trend, chart]);

  const hasData = chartData.some(
    (d) =>
      finiteNumber(d.avg) !== null ||
      finiteNumber(d.r) !== null ||
      finiteNumber(d.y) !== null ||
      finiteNumber(d.b) !== null
  );

  return (
    <section className="trends-section" aria-labelledby="trends-title">
      <div className="panel-heading chart-heading">
        <div>
          <span className="panel-kicker">Live pattern</span>
          <h2 id="trends-title">Current Trend</h2>
        </div>
        <label className="chart-select">
          <span className="sr-only">Chart metric</span>
          <select value={activeId} onChange={(event) => setActiveId(event.target.value)}>
            {CHARTS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label} {item.unit ? `(${item.unit})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="chart-surface" role="tabpanel">
        {!timestamps.length || !hasData ? (
          <div className="empty-chart">
            <Activity size={28} aria-hidden="true" />
            <p>No telemetry history available.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 20, right: 25, bottom: 10, left: 10 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="rawTime"
                tickFormatter={(val) => formatIstTimeOnly(val)}
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                minTickGap={45}
              />
              <YAxis
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                domain={['auto', 'auto']}
                unit={chart.unit ? ` ${chart.unit}` : ''}
                width={50}
              />
              <Tooltip
                content={<CustomChartTooltip unit={chart.unit} />}
                labelKey="rawTime"
              />

              {/* Shaded band for P05 - P95 expected range */}
              {p05 !== null && p95 !== null && (
                <ReferenceArea
                  y1={p05}
                  y2={p95}
                  fill="rgba(91, 188, 228, 0.14)"
                  stroke="none"
                  ifOverflow="extendDomain"
                />
              )}

              {/* Horizontal line for Baseline Median */}
              {baselineMedian !== null && (
                <ReferenceLine
                  y={baselineMedian}
                  stroke="#9aa8b7"
                  strokeDasharray="3 3"
                  strokeWidth={1.5}
                />
              )}

              {/* Optional Phase Lines */}
              {chart.hasPhases && showPhases && (
                <>
                  <Line
                    type="monotone"
                    dataKey="r"
                    name="R phase"
                    stroke={PHASE_COLORS.r}
                    strokeWidth={1.6}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="y"
                    name="Y phase"
                    stroke={PHASE_COLORS.y}
                    strokeWidth={1.6}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="b"
                    name="B phase"
                    stroke={PHASE_COLORS.b}
                    strokeWidth={1.6}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              )}

              {/* Average / Total Line */}
              <Line
                type="monotone"
                dataKey="avg"
                name={chart.hasPhases ? 'Average' : chart.label}
                stroke={chart.hasPhases ? PHASE_COLORS.avg : '#087f73'}
                strokeWidth={2.2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="chart-footer">
        <span>
          <i className="legend-line" /> Current
        </span>
        <span>
          <i className="legend-line baseline-line" /> Baseline median
        </span>
        {chart.hasPhases && (
          <label>
            <input
              type="checkbox"
              checked={showPhases}
              onChange={(event) => setShowPhases(event.target.checked)}
            />
            Show phase detail
          </label>
        )}
      </div>
      <p className="chart-note">Expected bands describe this pump's history, not equipment safety limits.</p>
    </section>
  );
}

export default TelemetryCharts;
